/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview OpenPay SDK script loading and client initialization helpers.
 * @module utils/openpayClient
 */

const OPENPAY_SCRIPT_URL = "https://js.openpay.mx/openpay.v1.min.js";
const OPENPAY_DATA_SCRIPT_URL = "https://js.openpay.mx/openpay-data.v1.min.js";
const DEFAULT_MAX_ATTEMPTS = 50;
const POLL_INTERVAL_MS = 100;

/**
 * Injects OpenPay script tags when the SDK is not already on window.
 * @returns {Promise<void>}
 */
export const loadOpenPaySDK = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Payment SDK unavailable"));
  }

  if (window.OpenPay) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = OPENPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      const dataScript = document.createElement("script");
      dataScript.src = OPENPAY_DATA_SCRIPT_URL;
      dataScript.async = true;
      dataScript.onload = () => resolve();
      dataScript.onerror = () =>
        reject(new Error("Failed to load OpenPay data script"));
      document.body.appendChild(dataScript);
    };
    script.onerror = () => reject(new Error("Failed to load OpenPay script"));
    document.body.appendChild(script);
  });
};

/**
 * Waits for the OpenPay SDK to load on window with a timeout.
 * @param {number} [maxAttempts=50] - Maximum polling attempts (100ms each)
 * @returns {Promise<void>}
 */
export const waitForOpenPaySDK = async (
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
) => {
  if (typeof window === "undefined") {
    throw new Error("Payment SDK unavailable");
  }

  let attempts = 0;
  while (typeof window.OpenPay === "undefined" && attempts < maxAttempts) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    attempts++;
  }

  if (typeof window.OpenPay === "undefined") {
    throw new Error("OpenPay SDK not loaded after timeout");
  }
};

/**
 * Returns the OpenPay SDK from window after verifying it is loaded.
 * @returns {typeof window.OpenPay}
 */
export const getOpenPayClient = () => {
  if (typeof window === "undefined" || typeof window.OpenPay === "undefined") {
    throw new Error("OpenPay SDK not loaded");
  }
  return window.OpenPay;
};

/**
 * Configures merchant credentials on the OpenPay SDK and returns the client.
 * @returns {typeof window.OpenPay}
 */
export const initializeOpenPayClient = () => {
  const OpenPay = getOpenPayClient();
  OpenPay.setId(process.env.REACT_APP_OPENPAY_MERCHANT_ID);
  OpenPay.setApiKey(process.env.REACT_APP_OPENPAY_PUBLIC_KEY);
  OpenPay.setSandboxMode(false);
  return OpenPay;
};
